<template>
  <div>
    <validation-observer ref="editCustomerOrderTypeAvailabilityValidation">
      <b-row class="match-height">
        <b-col cols="12">
          <b-card :title="$t('customer.order-type-availabilities.title')">
            <rc-overlay :show="loading">
              <div class="mb-2">
                {{ $t('customer.order-type-availabilities.info') }}
              </div>
              <form
                id="editCustomerOrderTypeAvailabilitiesForm"
                @submit.prevent
              >
                <div
                  v-for="row in availabilityRows"
                  :key="row.orderType"
                  class="py-1 border-bottom"
                >
                  <b-row class="align-items-center">
                    <b-col
                      cols="12"
                      md="4"
                    >
                      <div class="d-flex flex-column">
                        <span class="font-weight-bolder font-medium-2">
                          {{ $t(`order.types.${row.orderType}`) }}
                        </span>
                      </div>
                    </b-col>

                    <b-col
                      cols="12"
                      md="3"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :vid="`activeFrom-${row.orderType}`"
                        :name="$t('customer.order-type-availabilities.active-from')"
                        :rules="row.activeTo ? 'required' : ''"
                      >
                        <label :for="`activeFrom-${row.orderType}`">
                          {{ $t('customer.order-type-availabilities.active-from') }}
                        </label>
                        <flat-pickr
                          :id="`activeFrom-${row.orderType}`"
                          v-model="row.activeFrom"
                          class="form-control"
                          :config="datePickerConfig"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-col>

                    <b-col
                      cols="12"
                      md="3"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :vid="`activeTo-${row.orderType}`"
                        :name="$t('customer.order-type-availabilities.active-to')"
                        :rules="row.activeFrom ? `required|date_after_or_equal:${row.activeFrom || ''}` : ''"
                      >
                        <label :for="`activeTo-${row.orderType}`">
                          {{ $t('customer.order-type-availabilities.active-to') }}
                        </label>
                        <flat-pickr
                          :id="`activeTo-${row.orderType}`"
                          v-model="row.activeTo"
                          class="form-control"
                          :config="datePickerConfig"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-col>

                    <b-col
                      cols="12"
                      md="2"
                    >
                      <b-button
                        variant="outline-secondary"
                        class="btn-block mt-2"
                        @click="clearRowDates(row.orderType)"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="16"
                          class="mr-50"
                        />
                        {{ $t('shared.button.clear') }}
                      </b-button>
                    </b-col>
                  </b-row>
                </div>
              </form>
            </rc-overlay>

            <template #footer>
              <div class="text-right">
                <b-button
                  variant="primary"
                  :disabled="loading"
                  @click="submitForm"
                >
                  {{ $t('shared.button.submit') }}
                </b-button>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </validation-observer>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.min.css'
import { Polish as PolishLocale } from 'flatpickr/dist/l10n/pl'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { cloneNested, parseRequestError } from '@/helpers/helpers'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import orderTypes from '@/dictionaries/orderTypes'

export default {
  components: {
    RcOverlay,
    flatPickr,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    customer: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      availabilityRows: [],
      datePickerConfig: {
        locale: PolishLocale,
        enableTime: false,
        dateFormat: 'Y-m-d',
        disableMobile: true,
      },
    }
  },
  beforeMount() {
    this.init()
  },
  methods: {
    buildRows(configuredRows = []) {
      const configuredByType = configuredRows.reduce((accumulator, row) => ({
        ...accumulator,
        [row.orderType]: row,
      }), {})

      return Object.values(orderTypes).map(orderType => ({
        id: configuredByType[orderType]?.id || null,
        orderType,
        activeFrom: configuredByType[orderType]?.activeFrom || '',
        activeTo: configuredByType[orderType]?.activeTo || '',
      }))
    },
    hasConfiguredDateRange(row) {
      return !!row.activeFrom && !!row.activeTo
    },
    hasPartialDateRange(row) {
      return (!!row.activeFrom && !row.activeTo) || (!row.activeFrom && !!row.activeTo)
    },
    buildPartialDateErrors() {
      return this.availabilityRows.reduce((errors, row) => {
        if (!this.hasPartialDateRange(row)) {
          return errors
        }

        const nextErrors = { ...errors }

        if (!row.activeFrom) {
          nextErrors[`activeFrom-${row.orderType}`] = [this.$t('customer.order-type-availabilities.range-required')]
        }

        if (!row.activeTo) {
          nextErrors[`activeTo-${row.orderType}`] = [this.$t('customer.order-type-availabilities.range-required')]
        }

        return nextErrors
      }, {})
    },
    buildPayload() {
      return {
        customerOrderTypeAvailabilities: this.availabilityRows
          .filter(row => this.hasConfiguredDateRange(row))
          .map(row => ({
            orderType: row.orderType,
            activeFrom: row.activeFrom,
            activeTo: row.activeTo,
          })),
      }
    },
    clearRowDates(orderType) {
      this.availabilityRows = this.availabilityRows.map(row => (row.orderType === orderType
        ? {
          ...row,
          activeFrom: '',
          activeTo: '',
        }
        : row))
    },
    init() {
      this.loading = true
      this.$store.dispatch('customer/fetchCustomerOrderTypeAvailabilitiesByCustomerId', this.$route.params.id)
        .then(response => {
          this.availabilityRows = this.buildRows(response.data || [])
        })
        .catch(error => parseRequestError(error))
        .finally(() => {
          this.loading = false
        })
    },
    submitForm() {
      this.$refs.editCustomerOrderTypeAvailabilityValidation.validate()
        .then(valid => {
          const partialDateErrors = this.buildPartialDateErrors()
          if (Object.keys(partialDateErrors).length > 0) {
            this.$refs.editCustomerOrderTypeAvailabilityValidation.setErrors(partialDateErrors)
            return false
          }

          if (!valid) {
            return false
          }

          this.loading = true
          return this.$store.dispatch('customer/updateCustomerOrderTypeAvailabilities', {
            id: this.customer.id,
            data: cloneNested(this.buildPayload()),
          })
            .then(() => {
              window.toast.notify.success(this.$t('customer.notify.edit.success_title'))
            })
            .catch(error => {
              parseRequestError(error)
            })
            .finally(() => {
              this.loading = false
            })
        })
    },
  },
}
</script>
